import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  NgZone,
  inject,
} from '@angular/core';
import { AuxiliaresModule } from '../auxiliares.module';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import * as turf from '@turf/turf';
import { MapboxService } from '../mapbox/mapbox.service';
import { MapboxLayersService } from '../mapbox/mapbox-layers.service';
import { HelperService } from '../servicios/helper.service';
import { RecorridosService } from '../servicios/http/recorridos.service';
import { ITipoParada } from 'modelos/src';
import { environment } from '../../../environments/environment';
import proj4 from 'proj4';
import { aplicarEtiquetasMapa } from '../mapbox/etiquetas-mapa';

/**
 * Helper para cambiar a modo direct_select en Mapbox Draw
 * Necesario porque @types/mapbox__mapbox-gl-draw no incluye correctamente este overload
 */
function changeToDirectSelectMode(
  draw: MapboxDraw,
  featureId: string | number,
): void {
  // Mapbox Draw API oficial soporta este modo según documentación
  (draw.changeMode as (mode: string, options?: unknown) => void)(
    'direct_select',
    { featureId },
  );
}

export interface Parada {
  ubicacion: [number, number];
  nombre?: string;
  tipo?: ITipoParada;
}

export interface CreadorPolygon {
  coords: [number, number][];
  color?: string;
}

@Component({
  selector: 'app-mapa-creador-mapbox',
  imports: [AuxiliaresModule],
  templateUrl: './mapa-creador-mapbox.component.html',
  styleUrl: './mapa-creador-mapbox.component.scss',
})
export class MapaCreadorMapboxComponent
  implements OnInit, OnChanges, OnDestroy, AfterViewInit
{
  private mapboxService = inject(MapboxService);
  private mapboxLayersService = inject(MapboxLayersService);
  private helper = inject(HelperService);
  private recorridosService = inject(RecorridosService);
  private ngZone = inject(NgZone);

  // Referencia al contenedor del mapa
  @ViewChild('mapContainer', { static: false })
  mapContainer!: ElementRef<HTMLElement>;

  // Qué dibujar
  @Input() public dibujar: string[] = [];

  // LineString
  @Input() lineString: [number, number][] = [];
  @Output() lineStringChange = new EventEmitter<[number, number][]>();
  @Input() color = 'blue';

  // Icon -- Marker (Paradas)
  @Input() points: Parada[] = [];
  @Output() pointsChange = new EventEmitter<Parada[]>();
  @Input() pointLabel?: string;
  @Input() pointType = 'Paradas';
  @Input() checkDistanceFromLineString?: boolean;

  // Indicaciones
  @Input() indicaciones: string[] = [];

  // Polygons (fondo - zonas terminales)
  @Input() polygons: CreadorPolygon[] = [];

  public dibujando = false;

  // Mapbox
  private map: mapboxgl.Map | null = null;
  private draw: MapboxDraw | null = null;
  private blockClicks = false;

  // Source/Layer IDs
  private readonly PARADAS_SOURCE = 'paradas-source';
  private readonly PARADAS_LAYER = 'paradas-layer';
  private readonly PARADAS_LABEL_LAYER = 'paradas-label-layer';
  private readonly POLYGONS_SOURCE = 'polygons-creator-source';
  private readonly POLYGONS_LAYER = 'polygons-creator-layer';
  private readonly POLYGONS_OUTLINE_LAYER = 'polygons-creator-outline-layer';

  private async initMap(): Promise<void> {
    if (!this.mapContainer) {
      console.error('Map container not available');
      return;
    }

    // CRÍTICO: Crear mapa FUERA de Angular Zone para evitar que Mapbox dispare change detection
    // El loop de renderizado interno de Mapbox usa requestAnimationFrame, MessageChannel y Promises
    // que quedarían patcheados por Zone.js si se crean dentro de Angular Zone
    this.ngZone.runOutsideAngular(() => {
      // Crear nuevo mapa (no usar singleton para este caso)
      this.map = new mapboxgl.Map({
        language: 'es', // Etiquetas del mapa base en español
        container: this.mapContainer.nativeElement,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [-58.3816, -34.6037], // Buenos Aires default
        zoom: 13,
        accessToken: environment.mapboxToken,
        doubleClickZoom: false, // Deshabilitar zoom con doble click para permitir finalizar dibujo
      });
      aplicarEtiquetasMapa(this.map);

      // Agregar controles de navegación
      this.map.addControl(
        new mapboxgl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: true,
        }),
        'bottom-right',
      );

      this.map.on('load', async () => {
        await this.onMapLoaded();
      });
    });
  }

  private async onMapLoaded() {
    if (!this.map) return;

    // Cargar iconos necesarios
    await this.mapboxLayersService.loadAllIcons(this.map);

    // Cargar iconos específicos de paradas si no están cargados
    await this.loadParadaIcons();

    // Inicializar Mapbox Draw
    this.initDrawControls();

    // Inicializar sources y layers
    this.initializeLayers();

    // Si hay datos iniciales, cargarlos
    if (this.polygons.length > 0) {
      this.addPolygons();
    }

    if (this.lineString.length > 0) {
      this.addLineStringToMap();
    }

    if (this.points.length > 0) {
      this.updateParadasLayer();
    }

    // Setup event handlers
    this.setupEventHandlers();
  }

  private async loadParadaIcons(): Promise<void> {
    if (!this.map) return;

    const icons = [
      { name: 'parada-icon', url: 'assets/pins/parada.png' },
      { name: 'refugio-icon', url: 'assets/pins/refugio.png' },
    ];

    for (const icon of icons) {
      if (!this.map.hasImage(icon.name)) {
        try {
          const image = await this.loadImage(icon.url);
          this.map.addImage(icon.name, image);
        } catch (error) {
          console.error(`Error loading icon ${icon.name}:`, error);
        }
      }
    }
  }

  private loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });
  }

  private initDrawControls(): void {
    if (!this.map) return;

    const drawOptions: any = {
      displayControlsDefault: false,
      controls: {},
      styles: [
        // LineString activa
        {
          id: 'gl-draw-line-active',
          type: 'line',
          filter: [
            'all',
            ['==', '$type', 'LineString'],
            ['==', 'active', 'true'],
          ],
          paint: {
            'line-color': this.color || '#ff0000',
            'line-width': 4,
          },
        },
        // LineString inactiva
        {
          id: 'gl-draw-line-inactive',
          type: 'line',
          filter: [
            'all',
            ['==', '$type', 'LineString'],
            ['==', 'active', 'false'],
          ],
          paint: {
            'line-color': this.color || '#3bb2d0',
            'line-width': 4,
          },
        },
        // Vértices
        {
          id: 'gl-draw-polygon-and-line-vertex',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex']],
          paint: {
            'circle-radius': 6,
            'circle-color': '#fff',
            'circle-stroke-color': this.color || '#3bb2d0',
            'circle-stroke-width': 2,
          },
        },
      ],
    };

    this.draw = new MapboxDraw(drawOptions);
    this.map.addControl(this.draw);

    // Event listeners de draw
    this.map.on('draw.create', this.onDrawCreate.bind(this));
    this.map.on('draw.update', this.onDrawUpdate.bind(this));
    this.map.on('draw.delete', this.onDrawDelete.bind(this));
  }

  private initializeLayers(): void {
    if (!this.map) return;

    // Source y layers para polygons (fondo - terminales)
    if (!this.map.getSource(this.POLYGONS_SOURCE)) {
      this.map.addSource(this.POLYGONS_SOURCE, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
    }

    if (!this.map.getLayer(this.POLYGONS_LAYER)) {
      this.map.addLayer({
        id: this.POLYGONS_LAYER,
        type: 'fill',
        source: this.POLYGONS_SOURCE,
        paint: {
          'fill-color': this.color || 'blue',
          'fill-opacity': 0.1,
        },
      });
    }

    if (!this.map.getLayer(this.POLYGONS_OUTLINE_LAYER)) {
      this.map.addLayer({
        id: this.POLYGONS_OUTLINE_LAYER,
        type: 'line',
        source: this.POLYGONS_SOURCE,
        paint: {
          'line-color': this.color || 'blue',
          'line-width': 4,
        },
      });
    }

    // Source y layers para paradas (GeoJSON nativo, no HTML markers)
    if (!this.map.getSource(this.PARADAS_SOURCE)) {
      this.map.addSource(this.PARADAS_SOURCE, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
    }

    // Layer de íconos para paradas
    if (!this.map.getLayer(this.PARADAS_LAYER)) {
      this.map.addLayer({
        id: this.PARADAS_LAYER,
        type: 'symbol',
        source: this.PARADAS_SOURCE,
        layout: {
          'icon-image': [
            'match',
            ['get', 'tipo'],
            'refugio',
            'refugio-icon',
            'parada-icon', // default
          ],
          'icon-size': 0.15,
          'icon-allow-overlap': true,
          'icon-anchor': 'bottom',
        },
      });
    }

    // Layer de labels para paradas
    if (!this.map.getLayer(this.PARADAS_LABEL_LAYER)) {
      this.map.addLayer({
        id: this.PARADAS_LABEL_LAYER,
        type: 'symbol',
        source: this.PARADAS_SOURCE,
        layout: {
          'text-field': ['get', 'nombre'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
          'text-anchor': 'top',
          'text-offset': [0, 0.5],
        },
        paint: {
          'text-color': '#000',
          'text-halo-color': '#fff',
          'text-halo-width': 2,
        },
      });
    }
  }

  private setupEventHandlers(): void {
    if (!this.map) return;

    // Click en el mapa para agregar paradas (solo si no está dibujando)
    this.map.on('click', (e) => {
      // PRIMERO: verificar si los clicks están bloqueados o si estamos dibujando
      if (this.blockClicks || this.dibujando) {
        return;
      }

      // Verificar si el click fue en una parada existente
      const features = this.map!.queryRenderedFeatures(e.point, {
        layers: [this.PARADAS_LAYER],
      });

      if (features.length > 0) {
        // Click en una parada existente - no hacer nada aquí
        // El handler de click en parada se encarga
        return;
      }

      let ubicacion: [number, number] = [e.lngLat.lng, e.lngLat.lat];

      // Validaciones
      if (this.checkDistanceFromLineString && this.lineString.length === 0) {
        return this.helper.notifError(
          'Primero debe dibujar el recorrido',
          'Cerrar',
        );
      }

      if (this.checkDistanceFromLineString && this.lineString.length > 0) {
        const distancia = this.getDistanceFromLineString(ubicacion);

        if (distancia > 25) {
          return this.helper.notifError(
            'La parada debe estar cerca del recorrido',
            'Cerrar',
          );
        }

        // Ajustar la parada al punto más cercano en el recorrido (sobre la calle)
        ubicacion = this.snapPointToLineString(ubicacion);
      }

      const parada: Parada = { ubicacion };
      this.points.push(parada);

      this.updateParadasLayer();
      this.pointsChange.emit(this.points);
    });

    // Cambiar cursor al pasar sobre paradas
    this.map.on('mouseenter', this.PARADAS_LAYER, () => {
      if (this.map) {
        this.map.getCanvas().style.cursor = 'pointer';
      }
    });

    this.map.on('mouseleave', this.PARADAS_LAYER, () => {
      if (this.map) {
        this.map.getCanvas().style.cursor = '';
      }
    });

    // Click en parada para eliminar
    this.map.on('click', this.PARADAS_LAYER, async (e) => {
      if (this.dibujando || !e.features || e.features.length === 0) return;

      const feature = e.features[0];
      const index = feature.properties?.['index'];

      if (index !== undefined && index >= 0 && index < this.points.length) {
        // Prevenir que el click se propague al mapa
        e.preventDefault();

        // Eliminar la parada
        this.points.splice(index, 1);
        this.updateParadasLayer();
        this.pointsChange.emit(this.points);
      }
    });
  }

  // ========== LINESTRING DRAWING ==========

  public async dibujarLineString() {
    if (this.dibujando || !this.draw) return;
    this.dibujando = true;
    this.blockClicks = true; // Bloquear clicks mientras se dibuja

    // Limpiar features existentes en Draw
    this.draw.deleteAll();

    if (this.lineString.length > 0) {
      // Cargar linestring existente en Draw
      const feature: GeoJSON.Feature<GeoJSON.LineString> = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: this.lineString,
        },
        properties: {},
      };
      this.draw.add(feature);

      // Cambiar a modo de dibujo para continuar agregando vértices
      this.draw.changeMode('draw_line_string');
    } else {
      // Iniciar modo de dibujo
      this.draw.changeMode('draw_line_string');
    }
  }

  public async parar() {
    if (!this.draw) return;
    this.draw.changeMode('simple_select');

    if (this.checkDistanceFromLineString && this.points.length > 0) {
      this.checkPoints();
    }

    this.dibujando = false;
    // Desbloquear clicks después de un delay para asegurar que el doble click terminó
    setTimeout(() => {
      this.blockClicks = false;
    }, 300);
  }

  public async continuar() {
    if (!this.draw) return;

    if (this.lineString.length === 0) {
      this.draw.changeMode('draw_line_string');
      this.dibujando = true;
      this.blockClicks = true; // Bloquear clicks mientras se dibuja
    } else {
      // Asegurarse de que el feature esté en Draw
      let features = this.draw.getAll();

      if (features.features.length === 0 && this.lineString.length > 0) {
        // Si no hay features en Draw, agregar el lineString actual
        const feature: GeoJSON.Feature<GeoJSON.LineString> = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: this.lineString,
          },
          properties: {},
        };
        this.draw.add(feature);
        features = this.draw.getAll();
      }

      if (features.features.length > 0) {
        const featureId = features.features[0].id;
        changeToDirectSelectMode(this.draw, featureId);
        this.dibujando = true;
        this.blockClicks = true; // Bloquear clicks mientras se edita
      }
    }
  }

  public borrar() {
    if (!this.draw) return;

    this.draw.deleteAll();
    this.lineString = [];
    this.lineStringChange.emit(this.lineString);

    // Borrar paradas si es necesario
    if (this.checkDistanceFromLineString) {
      this.points = [];
      this.updateParadasLayer();
      this.pointsChange.emit(this.points);
    }

    this.dibujando = false;
    this.blockClicks = false; // Desbloquear inmediatamente al borrar todo
    if (this.draw) {
      this.draw.changeMode('simple_select');
    }
  }

  public eliminarUltimaCoordenada() {
    if (this.lineString.length === 0) return;

    this.lineString.pop();
    this.lineStringChange.emit(this.lineString);

    // Actualizar Draw
    this.draw?.deleteAll();
    if (this.lineString.length > 0) {
      this.addLineStringToMap();
    }

    this.checkPoints();

    if (this.lineString.length === 1) {
      this.borrar();
    }
  }

  public async snapToRoad() {
    if (this.lineString.length === 0) {
      return this.helper.notifError(
        'Primero debe dibujar el recorrido',
        'Cerrar',
      );
    }

    try {
      // Convertir de Mapbox (EPSG:4326 [lon, lat]) a OpenLayers (EPSG:3857)
      const lineStringOL = this.lineString.map((coord) => {
        const converted = proj4('EPSG:4326', 'EPSG:3857', coord);
        return [converted[0], converted[1]]; // Asegurar formato [x, y]
      });

      // Enviar al servicio
      const snappedOL = await this.recorridosService.snapToRoadOL(lineStringOL);

      if (snappedOL && snappedOL.length > 0) {
        // Convertir de OpenLayers (EPSG:3857) de vuelta a Mapbox (EPSG:4326 [lon, lat])
        this.borrar();
        this.lineString = snappedOL.map((coord) => {
          const converted = proj4('EPSG:3857', 'EPSG:4326', coord);
          return [converted[0], converted[1]] as [number, number];
        });
        this.lineStringChange.emit(this.lineString);
        this.addLineStringToMap();
      } else {
        this.helper.notifError(
          'Error al intentar ajustar el recorrido',
          'Cerrar',
        );
      }
    } catch (error) {
      console.error('Error en snapToRoad:', error);
      this.helper.notifError(
        'Error al intentar ajustar el recorrido',
        'Cerrar',
      );
    }
  }

  private addLineStringToMap() {
    if (this.lineString.length === 0 || !this.map || !this.draw) return;

    const feature: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: this.lineString,
      },
      properties: {},
    };

    // Agregar a Draw
    this.draw.deleteAll();
    this.draw.add(feature);

    // Fit bounds
    this.fitBoundsToLineString();
  }

  private fitBoundsToLineString() {
    if (this.lineString.length === 0 || !this.map) return;

    const bounds = new mapboxgl.LngLatBounds();
    this.lineString.forEach((coord) => {
      bounds.extend(coord as [number, number]);
    });

    this.map.fitBounds(bounds, { padding: 50 });
  }

  // ========== DRAW EVENT HANDLERS ==========

  private onDrawCreate(e: any): void {
    const features = e.features;
    if (features.length === 0) return;

    const feature = features[0];
    if (feature.geometry.type === 'LineString') {
      this.lineString = feature.geometry.coordinates;
      this.lineStringChange.emit(this.lineString);

      // Finalizar dibujo cuando se completa con doble click
      this.dibujando = false;

      // Bloquear clicks por 300ms para evitar que el segundo click del doble click agregue una parada
      this.blockClicks = true;
      setTimeout(() => {
        this.blockClicks = false;
      }, 300);
    }
  }

  private onDrawUpdate(e: any): void {
    const features = e.features;
    if (features.length === 0) return;

    const feature = features[0];
    if (feature.geometry.type === 'LineString') {
      this.lineString = feature.geometry.coordinates;
      this.lineStringChange.emit(this.lineString);
    }
  }

  private onDrawDelete(e: any): void {
    this.lineString = [];
    this.lineStringChange.emit(this.lineString);
  }

  // ========== PARADAS (GeoJSON LAYER) ==========

  /**
   * Actualiza la capa de paradas con los datos actuales de this.points
   */
  private updateParadasLayer() {
    if (!this.map) return;

    const source = this.map.getSource(
      this.PARADAS_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    if (!source) return;

    const features: GeoJSON.Feature<GeoJSON.Point>[] = this.points.map(
      (p, idx) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: p.ubicacion,
        },
        properties: {
          index: idx,
          nombre: p.nombre || `${this.pointLabel || 'Parada'} ${idx + 1}`,
          tipo: p.tipo || 'parada',
        },
      }),
    );

    source.setData({
      type: 'FeatureCollection',
      features,
    });
  }

  private checkPoints() {
    if (!this.checkDistanceFromLineString) return;
    if (!this.lineString.length) return;
    if (!this.points.length) return;

    const pointsToKeep: Parada[] = [];
    let removed = false;

    for (const p of this.points) {
      const distancia = this.getDistanceFromLineString(p.ubicacion);
      if (distancia <= 25) {
        pointsToKeep.push(p);
      } else {
        removed = true;
      }
    }

    if (removed) {
      this.points = pointsToKeep;
      this.helper.notifWarn(
        'Puntos eliminados por estar lejos de la línea',
        'Cerrar',
      );
      this.updateParadasLayer();
      this.pointsChange.emit(this.points);
    }
  }

  private getDistanceFromLineString(point: [number, number]): number {
    if (this.lineString.length === 0) return Infinity;

    const line = turf.lineString(this.lineString);
    const pt = turf.point(point);
    const snapped = turf.nearestPointOnLine(line, pt);

    return turf.distance(pt, snapped, { units: 'meters' });
  }

  /**
   * Ajusta un punto al punto más cercano en el lineString
   * @returns Punto ajustado al recorrido [lon, lat]
   */
  private snapPointToLineString(point: [number, number]): [number, number] {
    if (this.lineString.length === 0) return point;

    const line = turf.lineString(this.lineString);
    const pt = turf.point(point);
    const snapped = turf.nearestPointOnLine(line, pt);

    return snapped.geometry.coordinates as [number, number];
  }

  // ========== POLYGONS (FONDO) ==========

  private addPolygons() {
    if (!this.map || this.polygons.length === 0) return;

    const features: GeoJSON.Feature<GeoJSON.Polygon>[] = this.polygons.map(
      (p) => ({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [p.coords],
        },
        properties: {
          color: p.color || this.color,
        },
      }),
    );

    const source = this.map.getSource(
      this.POLYGONS_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    if (source) {
      source.setData({
        type: 'FeatureCollection',
        features,
      });
    }
  }

  // ========== LIFECYCLE HOOKS ==========

  ngOnInit(): void {
    // No inicializar el mapa aquí - esperar a ngAfterViewInit
  }

  ngAfterViewInit(): void {
    // El DOM está garantizado disponible aquí
    this.initMap();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['points'] && !changes['points'].firstChange) {
      this.updateParadasLayer();
    }

    if (changes['polygons'] && !changes['polygons'].firstChange) {
      this.addPolygons();
    }

    if (changes['lineString'] && !changes['lineString'].firstChange) {
      this.addLineStringToMap();
    }
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
}
